import { useEffect, useState } from 'react';

// components
import { BusinessCoachCard, CoachMessageType, showToast, ToastTypeEnum } from '@wonderschool/common-base-ui';
import DocumentUploader from '../../Components/Upload/DocumentUploader';
import FileShareModal from './modals/FileShareModal';
import UploadProgressModal from './modals/UploadProgressModal';
import FormsAndDocumentsActionButtons from './StaffActionButtons';
import FormsAndDocumentsTable from './StaffDocumentsTable';

// hooks
import { useDownloadFiles, useGetFiles, useUpdateFile, useUploadFile } from '@wonderschool/file-service-client';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../hooks/useOrganizations';

// util stuff
import {
  ParentHandbookConfirmationModal,
  ParentHandbookEditModal,
  SelectParentHandbookModal,
  useParentHandbookUpdater,
} from '@wonderschool/ccms-ui';
import { FileCategory, FileDocument, FileState, MalwareStatus } from '@wonderschool/common-base-types';
import { useParentHandbookQuery } from '@wonderschool/common-food-program-data-access';
import { useLocation } from 'react-router-dom';
import mimeTypes from '../../helpers/mimeTypes';
import { RouteNameEnum, useRoutes } from '../../navigation';
import { logError } from '../../rollbar';
import { allowedMimeTypes, FILE_ACTIONS_CONSTS, UPLOAD_STATE_CONSTS } from '../dictionary';
import ParentHandbookModal from './modals/ParentHandbookModal';

// descriptive variable to mask passing a boolean argument to modal control function
const MODAL_STATE_OPEN = true;

const StaffMain: React.FC = () => {
  const { t } = useTranslation();
  const { uploadFile, error: uploadError, isSuccess: uploadSuccess, reset: resetUploadHook } = useUploadFile();
  const { downloadFiles, error: downloadError } = useDownloadFiles();
  const { updateFile } = useUpdateFile();
  const { id: currentOrganizationId, marketplaceSchoolId } = useOrganization();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showParentHandbookConfirmation = queryParams.get('showParentHandbookConfirmation');

  const { handleFinish } = useParentHandbookUpdater();

  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UPLOAD_STATE_CONSTS | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<FileDocument[]>([]);
  const [openModalControl, setOpenModalControl] = useState<FILE_ACTIONS_CONSTS | null>(null);
  const [showParentHandbookModal, setShowParentHandbookModal] = useState(false);
  const [isEditParentHandbookModalOpen, setIsEditParentHandbookModalOpen] = useState(false);
  const [isSelectParentHandbookModalOpen, setIsSelectParentHandbookModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { gotoRouteByName } = useRoutes();
  const { data: parentHandbookResponse, loading: parentHandbookLoading } = useParentHandbookQuery();
  const { data: pdfDocuments } = useGetFiles({
    limit: 25,
    conditions: [
      {
        field: 'fileCategory',
        operator: 'in',
        value: [FileCategory.FORM],
      },
      {
        field: 'fileState',
        operator: '==',
        value: FileState.active,
      },
      {
        field: 'fileStatus',
        operator: '==',
        value: MalwareStatus.CLEAN,
      },
    ],
    orderBy: [
      {
        field: 'createdAt',
        direction: 'desc',
      },
    ],
  });

  // * filter out admin files - not all files have the isAdmin field so adding the filter to the query would exclude them
  const existingPDFFiles =
    pdfDocuments?.pages[0].documents.filter((doc) => doc.file.mimetype === mimeTypes.PDF && !doc.isAdmin) ?? [];

  const handleModalStates = (modalKey: FILE_ACTIONS_CONSTS, isOpen: boolean) => {
    setOpenModalControl(isOpen ? modalKey : null);
  };

  // upload stuff
  const handleFileSelectedForUpload = ({ data: file }: { data: File }) => {
    if (file) {
      setFileToUpload(file);
    }
  };

  const checkAndLogMissingMarketplaceSchoolId = () => {
    if (!parentHandbookResponse?.parentHandbook?.schoolId) {
      logError('Marketplace school id is missing from the organization', {
        organizationId: currentOrganizationId,
      });
    }
  };

  const handleOpenParentHandbookModal = () => {
    checkAndLogMissingMarketplaceSchoolId();
    setShowParentHandbookModal(true);
  };

  // Handle errors if download fails.
  useEffect(() => {
    if (!downloadError) return;

    // log it to rollbar
    logError('Documents - Download error', {
      status: downloadError?.status,
      message: downloadError?.message,
    });

    // show a toast
    showToast(ToastTypeEnum.Error, t('documents.staff.downloadError'));
  }, [downloadError, t]);

  // Handle errors if upload fails.
  useEffect(() => {
    if (!uploadError) return;

    // show the error modal state
    setUploadState(UPLOAD_STATE_CONSTS.error);
    setOpenModalControl(FILE_ACTIONS_CONSTS.upload);
    // log it with rollbar
    logError('Documents - Upload error: ', {
      serverMessage: uploadError,
    });
  }, [uploadError, setUploadState, setOpenModalControl]);

  // Handle upload success
  useEffect(() => {
    if (uploadSuccess && fileToUpload) {
      // show a toast
      showToast(
        ToastTypeEnum.Success,
        t('documents.staff.uploadSuccess', {
          fileName: fileToUpload?.name,
        })
      );
      // reset the file
      setFileToUpload(null);
      // reset the hook
      resetUploadHook();
    }
  }, [uploadSuccess, fileToUpload, resetUploadHook, setFileToUpload, t]);

  const handleFileSubmitted = async () => {
    if (!fileToUpload) {
      return null;
    }
    // set the modal content
    setUploadState(UPLOAD_STATE_CONSTS.uploading);
    // open progress modal
    handleModalStates(FILE_ACTIONS_CONSTS.upload, MODAL_STATE_OPEN);

    try {
      // start the request
      await uploadFile({
        file: fileToUpload,
        options: {
          fileAction: 'downloadable',
          fileCategory: FileCategory.FORM,
          metadata: {
            uploadPath: `/organizations/${currentOrganizationId}`,
          },
        },
      });

      // close the modal
      handleModalStates(FILE_ACTIONS_CONSTS.upload, !MODAL_STATE_OPEN);
    } catch (error) {
      // show the error modal state
      setUploadState(UPLOAD_STATE_CONSTS.error);
      // log it with rollbar
      logError('Documents - Upload error: ', {
        clientError: error,
        serverMessage: uploadError,
      });
    }

    return null;
  };

  // current docs stuff
  const handleSelectedDocuments = (selectedDocuments: FileDocument[]) => {
    setSelectedDocuments(selectedDocuments);
  };

  const handleFileActionClicked = (fileAction: FILE_ACTIONS_CONSTS) => {
    // Triggers file download.
    if (fileAction === FILE_ACTIONS_CONSTS.download) {
      downloadFiles(selectedDocuments.map((doc) => doc.id));
      return null;
    }

    handleModalStates(fileAction, true);
  };

  const navigateToParentHandbook = () => {
    gotoRouteByName(RouteNameEnum.PARENT_HANDBOOK);
  };

  const handleExistingParentHandbook = () => {
    setShowParentHandbookModal(false);
    setIsEditParentHandbookModalOpen(false);
    setIsSelectParentHandbookModalOpen(true);
  };

  const handleEditParentHandbook = () => {
    checkAndLogMissingMarketplaceSchoolId();
    setIsEditParentHandbookModalOpen(true);
  };

  const setExistingPDFAsParentHandbook = async (fileId: FileDocument['id'], shareHandbook?: boolean) => {
    try {
      updateFile(
        {
          id: fileId,
          fileCategory: FileCategory.PARENT_HANDBOOK,
        },
        {
          onSuccess: async () => {
            setIsConfirmationModalOpen(true);

            await handleFinish({
              shareHandbook,
            });
          },
          onError: (error) => {
            showToast(ToastTypeEnum.Error, t('parentHandbook.documentSetError'));
            logError('Error setting the document as parent handbook', {
              clientError: error,
              fileId,
            });
          },
        }
      );
      setIsSelectParentHandbookModalOpen(false);
    } catch (error) {
      showToast(ToastTypeEnum.Error, t('parentHandbook.documentSetError'));
      logError('Error setting the document as parent handbook', {
        clientError: error,
        fileId,
      });
    }
  };

  const handleNewParentHandbookUpload = async (file: File, shareHandbook?: boolean) => {
    try {
      await uploadFile({
        file,
        options: {
          fileAction: 'downloadable',
          fileCategory: FileCategory.PARENT_HANDBOOK,
          metadata: {
            uploadPath: `/organizations/${currentOrganizationId}`,
          },
        },
      });

      await handleFinish({
        shareHandbook,
      });
      resetUploadHook();
      setIsSelectParentHandbookModalOpen(false);
      setIsConfirmationModalOpen(true);
    } catch (error) {
      logError('Error uploading parent handbook', {
        clientError: error,
        file,
      });
      showToast(ToastTypeEnum.Error, t('common.uploadFileError'));
    }
  };

  // Open confirmation modal when redirecting from parent handbook page
  useEffect(() => {
    if (showParentHandbookConfirmation === 'true') {
      setIsConfirmationModalOpen(true);
    }
  }, [showParentHandbookConfirmation]);

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    clearParentHandbookConfirmationQueryParam();
  };

  const clearParentHandbookConfirmationQueryParam = () => {
    queryParams.delete('showParentHandbookConfirmation');
    const newSearch = queryParams.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    window.history.replaceState(null, '', newPath);
  };

  return (
    <>
      {/* modals */}
      <>
        {openModalControl === FILE_ACTIONS_CONSTS.share && (
          <FileShareModal
            isModalOpen
            selectedDocuments={selectedDocuments}
            closeModal={() => handleModalStates(FILE_ACTIONS_CONSTS.share, !MODAL_STATE_OPEN)}
          />
        )}
        {openModalControl === FILE_ACTIONS_CONSTS.upload && (
          <UploadProgressModal
            isModalOpen
            uploadState={uploadState}
            filename={fileToUpload?.name || ''}
            closeModal={() => handleModalStates(FILE_ACTIONS_CONSTS.upload, !MODAL_STATE_OPEN)}
          />
        )}
      </>
      <div className="flex flex-col space-y-4">
        {!parentHandbookLoading && !parentHandbookResponse?.parentHandbook?.id && (
          <>
            <BusinessCoachCard
              messageType={CoachMessageType.INFO}
              text={
                <p>
                  {t('parentHandbook.ctaDescription')}{' '}
                  <a
                    className="text-blue-900 hover:text-blue-900"
                    href="https://wonderschool.zendesk.com/hc/en-us/articles/31966002785431-Setting-up-your-Parent-handbook-and-collecting-parent-signature"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('parentHandbook.ctaLearnMore')}
                  </a>
                </p>
              }
              name={t('parentHandbook.ctaTitle')}
              ctaText={t('parentHandbook.cta')}
              onCtaClick={handleOpenParentHandbookModal}
            />
          </>
        )}
        {showParentHandbookModal && (
          <ParentHandbookModal
            isModalOpen={showParentHandbookModal}
            closeModal={() => setShowParentHandbookModal(false)}
            onExistingParentHandbook={handleExistingParentHandbook}
            marketplaceSchoolId={marketplaceSchoolId}
          />
        )}
        {isEditParentHandbookModalOpen && (
          <ParentHandbookEditModal
            isOpen={isEditParentHandbookModalOpen}
            onClose={() => setIsEditParentHandbookModalOpen(false)}
            onEdit={navigateToParentHandbook}
            onExistingParentHandbook={handleExistingParentHandbook}
            marketplaceSchoolId={marketplaceSchoolId}
          />
        )}
        {isSelectParentHandbookModalOpen && (
          <SelectParentHandbookModal
            isOpen={isSelectParentHandbookModalOpen}
            onClose={() => setIsSelectParentHandbookModalOpen(false)}
            existingPDFFiles={existingPDFFiles}
            onNewParentHandbookUpload={handleNewParentHandbookUpload}
            onExistingPDFSelected={setExistingPDFAsParentHandbook}
            marketplaceSchoolId={marketplaceSchoolId}
          />
        )}
        {isConfirmationModalOpen && (
          <ParentHandbookConfirmationModal isOpen={isConfirmationModalOpen} onClose={closeConfirmationModal} />
        )}
        <div className="flex flex-col justify-between gap-y-4 md:flex-row">
          <p>{t('documents.staff.description')}</p>
          {/* UPLOADER */}
          {selectedDocuments.length === 0 && (
            <DocumentUploader
              floated="none"
              onFileSelected={handleFileSelectedForUpload}
              onSubmit={handleFileSubmitted}
              onError={() => null}
              shouldUseExternalStorageApi
              title={t('documents.staff.uploadingTitle')}
              allowedFileTypes={allowedMimeTypes}
              maxNumberOfFiles={1}
            />
          )}
        </div>

        {/* forms/docs actions row */}
        <div className="flex flex-col">
          {/* SELECTED FILE ACTIONS */}
          {selectedDocuments.length > 0 && (
            <div data-testid="documents-action-btn-group" className="mt-4">
              <FormsAndDocumentsActionButtons
                areMultipleSelected={selectedDocuments.length > 1}
                handleFileActionClicked={handleFileActionClicked}
              />
            </div>
          )}
        </div>
        <div className="w-full">
          <FormsAndDocumentsTable
            onRowSelect={handleSelectedDocuments}
            onEditParentHandbook={handleEditParentHandbook}
          />
        </div>
      </div>
    </>
  );
};

export default StaffMain;
